(() => {
  const config = window.NasreenTheme || {};
  const liveRegion = document.querySelector('[data-live-region]');
  const cartDialog = document.querySelector('#CartDrawer');
  const cartItems = document.querySelector('[data-cart-items]');
  const cartFooter = document.querySelector('[data-cart-footer]');
  const cartTotal = document.querySelector('[data-cart-total]');
  const shippingMessage = document.querySelector('[data-shipping-message]');

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 40);
  };

  const formatMoney = (cents, currency = 'CAD') => new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
    style: 'currency',
    currency
  }).format((Number(cents) || 0) / 100);

  const updateCartCounts = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Shopping bag with ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  const shippingCopy = (cart) => {
    if (!shippingMessage) return;
    shippingMessage.textContent = 'Shipping rates and taxes are calculated at checkout.';
  };

  const renderCart = (cart) => {
    updateCartCounts(cart.item_count);
    shippingCopy(cart);
    if (!cartItems || !cartFooter || !cartTotal) return;

    cartFooter.hidden = cart.item_count === 0;
    cartTotal.textContent = formatMoney(cart.total_price, cart.currency);
    if (cart.item_count === 0) {
      cartItems.innerHTML = `
        <div class="empty-state">
          <p>Your cart is empty. Let’s change that.</p>
          <a class="button button--secondary" href="${escapeHtml(config.routes.allProducts)}">Shop skincare</a>
        </div>`;
      return;
    }

    cartItems.innerHTML = cart.items.map((item) => `
      <article class="cart-drawer__item">
        <a href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}&width=160" width="80" height="96" alt="${escapeHtml(item.product_title)}">` : ''}
        </a>
        <div>
          <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="fine-print">${escapeHtml(item.variant_title)}</p>` : ''}
          <div class="quantity" data-cart-line="${item.key}">
            <button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity">−</button>
            <input value="${item.quantity}" inputmode="numeric" aria-label="Quantity" readonly>
            <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase ${escapeHtml(item.product_title)} quantity">+</button>
          </div>
        </div>
        <strong>${formatMoney(item.final_line_price, cart.currency)}</strong>
      </article>`).join('');
  };

  const getCart = async () => {
    const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  const activeGalleryPanel = (gallery) => gallery?.querySelector('[data-product-gallery-panel]:not([hidden])');

  const updateGalleryDialog = (gallery) => {
    const dialog = gallery?.closest('.product-main')?.querySelector('[data-product-gallery-dialog]');
    if (!dialog?.open) return;
    const source = activeGalleryPanel(gallery)?.querySelector('img');
    const image = dialog.querySelector('[data-product-gallery-dialog-image]');
    const caption = dialog.querySelector('[data-product-gallery-dialog-caption]');
    if (!source || !image) return;
    image.src = source.currentSrc || source.src;
    image.alt = source.alt || '';
    if (caption) caption.textContent = source.alt || '';
  };

  const activateGalleryMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return false;
    const panels = [...gallery.querySelectorAll('[data-product-gallery-panel]')];
    const target = panels.find((panel) => panel.dataset.productGalleryPanel === String(mediaId));
    if (!target) return false;
    panels.forEach((panel) => {
      const active = panel === target;
      panel.hidden = !active;
      if (!active) panel.querySelector('video')?.pause();
    });
    gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((thumbnail) => {
      thumbnail.setAttribute('aria-selected', String(thumbnail.dataset.productGalleryThumb === String(mediaId)));
    });
    const zoom = gallery.querySelector('[data-product-gallery-open]');
    if (zoom) zoom.hidden = target.dataset.mediaType !== 'image';
    updateGalleryDialog(gallery);
    return true;
  };

  const openGalleryDialog = (gallery, trigger) => {
    const panel = activeGalleryPanel(gallery);
    if (panel?.dataset.mediaType !== 'image') return;
    const dialog = gallery.closest('.product-main')?.querySelector('[data-product-gallery-dialog]');
    if (!dialog) return;
    dialog._gallery = gallery;
    dialog._returnFocus = trigger;
    dialog.showModal();
    updateGalleryDialog(gallery);
  };

  const moveGalleryDialog = (dialog, direction) => {
    const gallery = dialog?._gallery;
    if (!gallery) return;
    const panels = [...gallery.querySelectorAll('[data-product-gallery-panel][data-media-type="image"]')];
    if (panels.length < 2) return;
    const current = activeGalleryPanel(gallery);
    const index = Math.max(0, panels.indexOf(current));
    const offset = direction === 'next' ? 1 : -1;
    const target = panels[(index + offset + panels.length) % panels.length];
    activateGalleryMedia(gallery, target.dataset.productGalleryPanel);
  };

  const openCart = async () => {
    if (!cartDialog) return;
    try { await refreshCart(); } catch (error) { announce(error.message); }
    if (!cartDialog.open) cartDialog.showModal();
  };

  document.addEventListener('click', async (event) => {
    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger && config.cartDrawer) {
      event.preventDefault();
      await openCart();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      cartDialog?.close();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-cart-line]');
      quantityButton.disabled = true;
      try {
        const response = await fetch(`${config.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.dataset.cartLine, quantity: Number(quantityButton.dataset.cartQuantity) })
        });
        if (!response.ok) throw new Error('Cart quantity could not be updated.');
        renderCart(await response.json());
        announce('Cart updated.');
      } catch (error) {
        announce(error.message);
      } finally {
        quantityButton.disabled = false;
      }
      return;
    }

    const quantityControl = event.target.closest('[data-quantity-change]');
    if (quantityControl) {
      const wrapper = quantityControl.closest('[data-quantity]');
      const input = wrapper?.querySelector('input');
      if (!input) return;
      const next = Math.max(1, Number(input.value || 1) + Number(quantityControl.dataset.quantityChange));
      input.value = next;
      return;
    }

    const galleryThumbnail = event.target.closest('[data-product-gallery-thumb]');
    if (galleryThumbnail) {
      const gallery = galleryThumbnail.closest('[data-product-gallery]');
      activateGalleryMedia(gallery, galleryThumbnail.dataset.productGalleryThumb);
      return;
    }

    const galleryOpen = event.target.closest('[data-product-gallery-open]');
    if (galleryOpen) {
      openGalleryDialog(galleryOpen.closest('[data-product-gallery]'), galleryOpen);
      return;
    }

    const galleryClose = event.target.closest('[data-product-gallery-close]');
    if (galleryClose) {
      galleryClose.closest('[data-product-gallery-dialog]')?.close();
      return;
    }

    const galleryDirection = event.target.closest('[data-product-gallery-direction]');
    if (galleryDirection) {
      moveGalleryDialog(galleryDirection.closest('[data-product-gallery-dialog]'), galleryDirection.dataset.productGalleryDirection);
      return;
    }

    const railControl = event.target.closest('[data-rail-direction]');
    if (railControl) {
      const rail = document.querySelector(`#${CSS.escape(railControl.getAttribute('aria-controls'))}`);
      const direction = railControl.dataset.railDirection === 'next' ? 1 : -1;
      rail?.scrollBy({ left: direction * Math.min(rail.clientWidth * 0.85, 400), behavior: 'smooth' });
    }
  });

  cartDialog?.addEventListener('click', (event) => {
    if (event.target === cartDialog) cartDialog.close();
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!config.cartDrawer) return;
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const original = submit?.textContent;
      if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; }
      try {
        const response = await fetch(`${config.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) {
          const payload = await response.json();
          throw new Error(payload.description || 'This item could not be added.');
        }
        await openCart();
        announce('Added to your cart.');
      } catch (error) {
        announce(error.message);
      } finally {
        if (submit) { submit.disabled = false; submit.textContent = original; }
      }
    });

    const selector = form.querySelector('[data-variant-select]');
    selector?.addEventListener('change', () => {
      const option = selector.selectedOptions[0];
      const submit = form.querySelector('[type="submit"]');
      const price = document.querySelector('[data-product-price]');
      const available = option.dataset.available === 'true';
      if (submit) {
        submit.disabled = !available;
        submit.textContent = available ? 'Add to cart' : 'Sold out';
      }
      if (price) price.textContent = option.dataset.price;
      if (option.dataset.mediaId) activateGalleryMedia(form.closest('.product-main')?.querySelector('[data-product-gallery]'), option.dataset.mediaId);
      if (option.dataset.url) window.history.replaceState({}, '', option.dataset.url);
    });
  });

  document.querySelectorAll('[data-product-gallery-dialog]').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog._returnFocus?.focus();
      dialog._returnFocus = null;
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') moveGalleryDialog(dialog, 'previous');
      if (event.key === 'ArrowRight') moveGalleryDialog(dialog, 'next');
    });
  });

  const menuToggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  const closeMenu = (restoreFocus = false) => {
    const focusWasInside = menu?.contains(document.activeElement);
    menu?.classList.remove('is-open');
    menuToggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && focusWasInside) menuToggle?.focus();
  };
  menuToggle?.addEventListener('click', () => {
    const open = !menu?.classList.contains('is-open');
    menu?.classList.toggle('is-open', open);
    menuToggle.setAttribute('aria-expanded', String(open));
  });
  menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu(true);
      if (cartDialog?.open) cartDialog.close();
    }
  });

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  if (!reducedMotion.matches && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8%', threshold: 0.08 });
  }

  const revealWithin = (root) => {
    const items = [
      ...(root.matches?.('[data-reveal], [data-parity-reveal]') ? [root] : []),
      ...root.querySelectorAll?.('[data-reveal], [data-parity-reveal]') || [],
    ];
    items.forEach((item) => {
      if (item.classList.contains('is-visible')) return;
      if (revealObserver) revealObserver.observe(item);
      else item.classList.add('is-visible');
    });
  };
  revealWithin(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) revealWithin(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });

  document.querySelectorAll('[data-reference-tabs]').forEach((tabs) => {
    const controls = [...tabs.querySelectorAll('[data-reference-tab]')];
    const panels = [...tabs.querySelectorAll('[data-reference-panel]')];
    controls.forEach((control) => control.addEventListener('click', () => {
      const target = control.dataset.referenceTab;
      controls.forEach((item) => {
        const active = item === control;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      panels.forEach((panel) => {
        const active = panel.dataset.referencePanel === target;
        panel.classList.toggle('is-active', active);
        const video = panel.querySelector('video');
        if (video) active ? video.play().catch(() => {}) : video.pause();
      });
    }));
  });

  document.querySelectorAll('[data-parity-carousel]').forEach((carousel) => {
    const slides = [...carousel.querySelectorAll('.parity-hero-slide')];
    const controls = [...carousel.querySelectorAll('[data-parity-slide]')];
    let activeIndex = 0;
    let timer;
    const activate = (index, announceChange = false) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
      controls.forEach((control, controlIndex) => control.setAttribute('aria-pressed', String(controlIndex === activeIndex)));
      if (announceChange) announce(`Showing testimonial ${activeIndex + 1} of ${slides.length}.`);
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (!reducedMotion.matches && !carousel.hasAttribute('data-motion-paused')) timer = window.setInterval(() => activate(activeIndex + 1), 2750);
    };
    controls.forEach((control) => control.addEventListener('click', () => {
      activate(Number(control.dataset.paritySlide), true);
      start();
    }));
    carousel.addEventListener('pointerenter', stop);
    carousel.addEventListener('pointerleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    activate(0);
    start();

    const motionToggle = carousel.querySelector('[data-parity-motion-toggle]');
    motionToggle?.addEventListener('click', () => {
      const paused = !carousel.hasAttribute('data-motion-paused');
      carousel.toggleAttribute('data-motion-paused', paused);
      document.documentElement.classList.toggle('parity-motion-paused', paused || reducedMotion.matches);
      motionToggle.setAttribute('aria-pressed', String(paused));
      motionToggle.textContent = paused ? 'Play motion' : 'Pause motion';
      if (paused) stop(); else start();
    });
    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches) stop(); else start();
    });
  });

  document.querySelectorAll('[data-parity-tabs]').forEach((tabs) => {
    const controls = [...tabs.querySelectorAll('[data-parity-tab]')];
    const accordionControls = controls.length ? [] : [...tabs.children].filter((child) => child.matches('button'));
    if (controls.some((control) => control.getAttribute('role') === 'tab')) {
      tabs.setAttribute('role', 'tablist');
      if (!tabs.getAttribute('aria-label')) tabs.setAttribute('aria-label', 'Interactive content');
    }
    controls.forEach((control) => {
      if (!control.getAttribute('aria-label')) {
        const label = control.querySelector('.text-h5, .text-h4, .accordion-title')?.textContent?.trim();
        if (label) control.setAttribute('aria-label', label);
      }
    });
    controls.forEach((control) => control.addEventListener('click', () => {
      controls.forEach((item) => {
        const active = item === control;
        item.classList.toggle('w--current', active);
        item.setAttribute('aria-selected', String(active));
      });
    }));
    accordionControls.forEach((control) => control.addEventListener('click', () => {
      accordionControls.forEach((item) => {
        const active = item === control;
        item.setAttribute('aria-expanded', String(active));
        const panel = item.nextElementSibling;
        if (panel) panel.hidden = !active;
      });
    }));
  });

  const deferredVideoLoaders = [];
  document.querySelectorAll('[data-parity-video-deferred]').forEach((video) => {
    let loaded = false;
    const loadVideo = () => {
      if (loaded || reducedMotion.matches) return;
      loaded = true;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
      video.play().catch(() => {});
    };
    deferredVideoLoaders.push(loadVideo);
    if (window.matchMedia('(min-width: 768px)').matches) {
      window.addEventListener('load', () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(loadVideo, { timeout: 3000 });
        else window.setTimeout(loadVideo, 500);
      }, { once: true });
    } else {
      ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, loadVideo, { once: true, passive: true });
      });
    }
  });

  const videoDialog = document.createElement('dialog');
  videoDialog.className = 'parity-video-dialog';
  videoDialog.setAttribute('aria-label', 'Video player');
  videoDialog.innerHTML = '<button type="button" class="parity-video-dialog__close" aria-label="Close video">×</button><div class="parity-video-dialog__frame"></div>';
  document.body.append(videoDialog);
  const videoFrame = videoDialog.querySelector('.parity-video-dialog__frame');
  const closeVideoDialog = () => videoDialog.close();
  videoDialog.querySelector('.parity-video-dialog__close').addEventListener('click', closeVideoDialog);
  videoDialog.addEventListener('click', (event) => { if (event.target === videoDialog) closeVideoDialog(); });
  videoDialog.addEventListener('close', () => { videoFrame.replaceChildren(); videoDialog._returnFocus?.focus(); });
  document.querySelectorAll('[data-video-lightbox]').forEach((trigger) => trigger.addEventListener('click', () => {
    const rawUrl = trigger.dataset.videoLightbox;
    const match = rawUrl.match(/vimeo\.com\/(\d+)(?:\/([a-z0-9]+))?/i);
    if (!match) { window.open(rawUrl, '_blank', 'noopener'); return; }
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${match[1]}${match[2] ? `?h=${match[2]}&` : '?'}autoplay=1&dnt=1`;
    iframe.title = 'Nasreen Organics story';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    videoFrame.replaceChildren(iframe);
    videoDialog._returnFocus = trigger;
    videoDialog.showModal();
  }));

  const applyReducedMotion = (event) => {
    if (reducedMotion.matches) document.querySelectorAll('video').forEach((video) => video.pause());
    else if (event) deferredVideoLoaders.forEach((loadVideo) => loadVideo());
    const manuallyPaused = Boolean(document.querySelector('[data-motion-paused]'));
    document.documentElement.classList.toggle('parity-motion-paused', reducedMotion.matches || manuallyPaused);
  };
  applyReducedMotion();
  reducedMotion.addEventListener?.('change', applyReducedMotion);
})();
